import { ReactNode } from "react";
import { Box, Dialog, DialogContent, Typography, alpha } from "@mui/material";
import { Button } from "./Button";

const NOTA_TEXT = "#5C6B7C";
const DIALOG_SCRIM = "rgba(6, 44, 78, 0.55)";

export type DialogConfirmTone = "primary" | "danger" | "success";

interface Props {
  open: boolean;
  title: string;
  /** Texto principal; acepta nodos para resaltar partes con `<strong>`. */
  message: ReactNode;
  /** Ícono dentro del círculo de color de `tone`. Sin ícono no se dibuja el círculo. */
  icon?: ReactNode;
  /** `danger` pinta el ícono en rojo y el botón de confirmar en oscuro (acción destructiva). */
  tone?: DialogConfirmTone;
  confirmLabel: string;
  /** Sin `cancelLabel` el modal solo tiene el botón de confirmar (avisos de resultado). */
  cancelLabel?: string;
  /** Texto del botón de confirmar mientras `loading`. */
  loadingLabel?: string;
  /** Mientras carga: botones deshabilitados y el modal no se puede cerrar. */
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Contenido extra entre el mensaje y los botones (p. ej. una comparación de montos). */
  children?: ReactNode;
  confirmTestId: string;
  cancelTestId?: string;
}

/**
 * Modal de confirmación / aviso del wizard v2. Mismo spec que los diálogos de ofertas
 * (`DialogDescartarOferta`): scrim azul con blur, radio 18, contenido centrado y botones a lo ancho.
 * Cada pantalla le pasa sus textos en lugar de tener su propio componente de modal.
 */
export const DialogConfirm = ({
  open,
  title,
  message,
  icon,
  tone = "primary",
  confirmLabel,
  cancelLabel,
  loadingLabel,
  loading = false,
  onConfirm,
  onClose,
  children,
  confirmTestId,
  cancelTestId,
}: Props) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    fullWidth
    PaperProps={{ sx: { maxWidth: 440, m: 2, borderRadius: "18px", boxShadow: "0 24px 64px rgba(6, 44, 78, 0.35)" } }}
    slotProps={{ backdrop: { sx: { bgcolor: DIALOG_SCRIM, backdropFilter: "blur(4px)" } } }}
  >
    <DialogContent sx={{ p: "30px 28px", textAlign: "center" }}>
      {icon && (
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: "auto",
            mb: "16px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tone === "danger" ? "error.main" : tone === "success" ? "primary.accent" : "primary.main",
            bgcolor: (theme) =>
              alpha(
                tone === "danger"
                  ? theme.palette.error.main
                  : tone === "success"
                    ? theme.palette.primary.accent
                    : theme.palette.primary.main,
                0.12,
              ),
          }}
        >
          {icon}
        </Box>
      )}

      <Typography sx={{ fontWeight: 600, fontSize: 20, color: "primary.dark", mb: "10px" }}>{title}</Typography>
      <Typography
        component="div"
        sx={{ fontSize: 14.5, lineHeight: 1.55, color: NOTA_TEXT, mb: "24px", "& strong": { color: "primary.dark" } }}
      >
        {message}
      </Typography>

      {children && <Box sx={{ mb: "24px" }}>{children}</Box>}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        {cancelLabel && (
          <Button tone="outline" fullWidth disabled={loading} onClick={onClose} data-testid={cancelTestId}>
            {cancelLabel}
          </Button>
        )}
        <Button
          tone={tone === "danger" ? "dark" : "primary"}
          fullWidth
          disabled={loading}
          onClick={onConfirm}
          data-testid={confirmTestId}
        >
          {loading && loadingLabel ? loadingLabel : confirmLabel}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);
